import _get from 'lodash/get'
import { t } from 'i18next'

const getErrorMessage = (error: Error) => {
  return _get(
    error,
    'response.data.message',
    error?.message || t('key_oops_an_error_occurred'),
  )
}

const getErrorCode = (error: Error) => {
  return _get(error, 'response.data.code', '')
}

const getErrorStatus = (error: Error): number => {
  return _get(error, 'response.status', 0)
}

/**
 * Whether the request lost a race with one the user already made.
 *
 * `POST /financial/withdraw` and `POST /financial/bank-account` answer 409
 * `CONFLICT` when a request of the same kind is already waiting on Owner
 * approval. Neither is retryable: nothing the user does here succeeds until the
 * pending one is decided, so callers must say so rather than offer a retry.
 *
 * Both signals are checked because the two are populated independently — the
 * api-client interceptor rejects HTTP failures as the raw axios error, so
 * `response.status` is always present while `response.data.code` depends on the
 * BE sending a body. Matching either means a body-less 409 is still recognised.
 *
 * Note `error.code` (top level) is NOT the BE's code — on an axios rejection
 * that is axios's own identifier, e.g. 'ERR_BAD_REQUEST'.
 */
const isConflictError = (error: Error): boolean =>
  getErrorStatus(error) === 409 || getErrorCode(error) === 'CONFLICT'

/**
 * Renders a failed request as a single line for Sentry.
 *
 * `sendLog` in helpers/log takes a string, not an error — handing it the error
 * object lands `[object Object]` in Sentry, which is what several existing
 * callers do. `context` identifies the caller, since the error alone does not
 * say which screen fired the request.
 */
const formatApiErrorLog = (error: Error, context: string): string =>
  `[${context}] request failed: [${
    getErrorStatus(error) || '?'
  }] ${getErrorMessage(error)}`

export {
  formatApiErrorLog,
  getErrorCode,
  getErrorMessage,
  getErrorStatus,
  isConflictError,
}
